import { useCallback, useMemo, useSyncExternalStore } from 'react';
import {
  Dimensions,
  ImageStyle,
  Platform,
  StyleSheet,
  TextStyle,
  useWindowDimensions,
  ViewStyle,
  type ScaledSize,
} from 'react-native';

import { computeScaling, type ScaleSize } from '@/theme/scaling';

export const WEB_SMART_APP_BANNER_LAYOUT_EVENT = 'smart-app-banner-layout';

declare global {
  interface Window {
    __SMART_APP_BANNER_HEIGHT__?: number;
  }
}

type WindowSize = Pick<ScaledSize, 'width' | 'height'>;

function getWebBannerHeight() {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return 0;
  return Math.max(0, window.__SMART_APP_BANNER_HEIGHT__ ?? 0);
}

function getAdjustedWebWindowSize(size: WindowSize, bannerHeight = getWebBannerHeight()) {
  return {
    width: size.width,
    height: Math.max(0, size.height - bannerHeight),
  };
}

type WebWindowSizeListener = () => void;

// Keep one stable web viewport snapshot for the whole app. If every useScaling() call owns its
// own state, a component mounted after the on-screen keyboard opens initializes from the reduced
// window height and receives a smaller scale than components that were already mounted.
let stableWebRawWindowSize: WindowSize = Dimensions.get('window');
let webBannerHeight = getWebBannerHeight();
let webWindowSizeSnapshot = getAdjustedWebWindowSize(stableWebRawWindowSize, webBannerHeight);
const webWindowSizeListeners = new Set<WebWindowSizeListener>();
let webDimensionsSubscription: ReturnType<typeof Dimensions.addEventListener> | null = null;
let webResizeRafId: number | null = null;
let pendingWebRawWindowSize: WindowSize | null = null;

function emitWebWindowSizeIfChanged() {
  const next = getAdjustedWebWindowSize(stableWebRawWindowSize, webBannerHeight);
  if (next.width === webWindowSizeSnapshot.width && next.height === webWindowSizeSnapshot.height) {
    return;
  }

  webWindowSizeSnapshot = next;
  webWindowSizeListeners.forEach(listener => listener());
}

function applyWebRawWindowSize(next: WindowSize) {
  if (
    stableWebRawWindowSize.width === next.width &&
    stableWebRawWindowSize.height === next.height
  ) {
    return;
  }

  // The mobile web keyboard reduces height without changing width. Keep the last stable layout
  // height so newly mounted components use the same scale as the rest of the screen. Width changes
  // (rotation / responsive resize) and height growth (keyboard dismissed) remain responsive.
  if (stableWebRawWindowSize.width === next.width && next.height < stableWebRawWindowSize.height) {
    return;
  }

  stableWebRawWindowSize = next;
  emitWebWindowSizeIfChanged();
}

function scheduleWebRawWindowSize(next: WindowSize) {
  pendingWebRawWindowSize = next;
  if (webResizeRafId != null) return;

  webResizeRafId = requestAnimationFrame(() => {
    webResizeRafId = null;
    const pending = pendingWebRawWindowSize;
    pendingWebRawWindowSize = null;
    if (pending) applyWebRawWindowSize(pending);
  });
}

function handleWebSmartAppBannerLayout() {
  // Only the banner inset changes here. Do not re-read Dimensions: this event may fire while the
  // keyboard is open, when Dimensions contains the transient reduced height.
  webBannerHeight = getWebBannerHeight();
  emitWebWindowSizeIfChanged();
}

function subscribeWebWindowSize(listener: WebWindowSizeListener) {
  webWindowSizeListeners.add(listener);

  if (webWindowSizeListeners.size === 1) {
    webDimensionsSubscription = Dimensions.addEventListener('change', ({ window: next }) => {
      scheduleWebRawWindowSize(next);
    });

    if (typeof window !== 'undefined') {
      window.addEventListener(WEB_SMART_APP_BANNER_LAYOUT_EVENT, handleWebSmartAppBannerLayout);
      handleWebSmartAppBannerLayout();
    }

    // Reconcile real width changes that may have happened while there were no subscribers. A
    // height-only decrease is still treated as the keyboard and intentionally ignored.
    applyWebRawWindowSize(Dimensions.get('window'));
  }

  return () => {
    webWindowSizeListeners.delete(listener);
    if (webWindowSizeListeners.size > 0) return;

    webDimensionsSubscription?.remove();
    webDimensionsSubscription = null;
    if (typeof window !== 'undefined') {
      window.removeEventListener(WEB_SMART_APP_BANNER_LAYOUT_EVENT, handleWebSmartAppBannerLayout);
    }
    if (webResizeRafId != null) {
      cancelAnimationFrame(webResizeRafId);
      webResizeRafId = null;
      pendingWebRawWindowSize = null;
    }
  };
}

function getWebWindowSizeSnapshot() {
  return webWindowSizeSnapshot;
}

/**
 * Shared reactive window size for web. Resize bursts are coalesced to one update per animation
 * frame, and all useScaling() consumers receive the same keyboard-stable snapshot.
 */
function useResponsiveWindowSize(): WindowSize {
  return useSyncExternalStore(
    subscribeWebWindowSize,
    getWebWindowSizeSnapshot,
    getWebWindowSizeSnapshot,
  );
}

const useWindowDimensionsPlatform =
  Platform.OS === 'web' ? useResponsiveWindowSize : useWindowDimensions;

/**
 * Reactive scaling hook — recomputes on dimension changes (rotation, split-view).
 * Drop-in replacement for the static `scale` / `s` / `scaleVertical` functions.
 */
export function useScaling() {
  const { width, height } = useWindowDimensionsPlatform();

  const metrics = useMemo(() => computeScaling(width, height), [width, height]);

  const scale = useCallback(
    (size: ScaleSize): number => {
      if (typeof size === 'number') {
        return size * metrics.scaleFactor;
      }
      return metrics.isTablet
        ? size.tablet * metrics.scaleFactor
        : size.phone * metrics.scaleFactor;
    },
    [metrics.scaleFactor, metrics.isTablet],
  );
  const scaleVertical = useCallback(
    (size: number): number => size * metrics.scaleHeight,
    [metrics.scaleHeight],
  );

  return useMemo(
    () => ({
      scale,
      scaleVertical,
      ...metrics,
    }),
    [scale, scaleVertical, metrics],
  );
}

export type Scaling = ReturnType<typeof useScaling>;

type RNStyle = ViewStyle | TextStyle | ImageStyle;
type NamedStyles = Record<string, RNStyle>;

/**
 * Factory that returns a `useStyles` hook with reactive scaling.
 *
 * @example
 * const useStyles = createStyles(({ scale }) => ({
 *   label: { marginTop: scale(2), fontSize: scale(8) },
 * }));
 *
 * function MyComponent() {
 *   const styles = useStyles();
 *   return <Text style={styles.label} />;
 * }
 */
export function createStyles<T extends NamedStyles>(factory: (scaling: Scaling) => T) {
  return (): T => {
    const scaling = useScaling();
    return useMemo(() => StyleSheet.create(factory(scaling)), [scaling]);
  };
}
